<template>
  <FormQuestion
    @back="$emit('back')"
    @next="validateAndNext"
    :header="$bct('Child') + ` - ${childData.name}`"
    :hide-actions="hideActions"
    :next-disabled="nextDisabled"
    :processing="processing"
    :subtitle="subsidyProgram.preference_subtitle"
    :title="subsidyProgram.preference_title"
  >
    <ProviderReferredProgramCard
      v-if="referredProvider && showReferredProviderCard && step === 1"
      @confirm="(programId) => handleReferredProgramConfirm(programId)"
      @open="openProvider(referredProvider?.id)"
      :provider="referredProvider"
      class="mb-5"
      data-testid="providerReferredProgramCard"
    />

    <v-stepper-vertical
      v-model="step"
      class="pb-0 full-width"
      flat
      hide-actions
      tile
    >
      <v-card
        class="mb-4 focus-very-visible w-100pc"
        data-testid="selectProgramsStep"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <div
            ref="stepHeader"
            class="focus-very-visible step-header"
            tabindex="0"
          >
            <v-stepper-vertical-item
              :complete="step > 1"
              value="1"
            >
              <template #title>
                <h3 tabindex="0">
                  {{ $bct(subsidyProgram.select_providers_title || '') }}
                </h3>
                <h4
                  class="fs-16 c-light-black"
                  tabindex="0"
                >
                  {{ $bct(subsidyProgram.select_providers_subtitle || '') }}
                </h4>
              </template>

              <SelectProgramsStep
                @check="verifySelectionLimit"
                @copy="copyPreferences"
                @move="$emit('reorderPrograms', $event[0], $event[0] + $event[1])"
                @next="validateStepChange(2)"
                @open="openProvider($event)"
                @remove="remove"
                @toggle="toggleSelection($event[0], $event[1])"
                ref="stepComponent"
                :api="api"
                :child="childData"
                :default-query="defaultQuery"
                :previous-child-name="previousChildName"
                :previous-subsidy-program-ids="previousSubsidyProgramIds"
                :processing="processing"
                :program-ids="subsidy.program_ids"
                :show-selected="showSelected"
                :subsidy="subsidy"
                :subsidy-program="subsidyProgram"
              />
            </v-stepper-vertical-item>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="mb-4 w-100pc"
        data-testid="rankProgramsStep"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <div
            ref="stepHeader"
            class="focus-very-visible step-header"
            tabindex="0"
          >
            <v-stepper-vertical-item
              :complete="step > 2"
              value="2"
            >
              <template #title>
                <h3 tabindex="0">
                  {{ $bct(subsidyProgram.rank_providers_title || '') }}
                </h3>
                <h4
                  class="fs-16 c-light-black"
                  tabindex="0"
                >
                  {{ $bct(subsidyProgram.rank_providers_subtitle || '') }}
                </h4>
              </template>

              <RankProgramsStep
                @back="changeStep(step - 1)"
                @move="$emit('reorderPrograms', $event[0], $event[0] + $event[1])"
                @next="changeStep(step + 1)"
                @open="openProvider($event)"
                @remove="remove"
                @toggle="remove($event[0])"
                ref="stepComponent"
                :api="api"
                :hide-remove="true"
                :program-ids="subsidy.program_ids"
                :show-next="showNextOnStep(step)"
                :subsidy-program="subsidyProgram"
              />
            </v-stepper-vertical-item>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        v-if="conditionalSteps?.includes('current_provider_id')"
        class="mb-4 w-100pc"
        data-testid="currentEnrollmentStep"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <v-stepper-vertical-item
            :complete="step > conditionalSteps.indexOf('current_provider_id')"
            :value="conditionalSteps.indexOf('current_provider_id')"
          >
            <template #title>
              <h3 tabindex="0">
                {{ $bct(subsidyProgram.current_provider_title || '') }}
              </h3>
            </template>
            <CurrentEnrollmentStep
              v-model="currentProviderId"
              @back="changeStep(step - 1)"
              @na="currentProviderNotApplicableClicked"
              @next="changeStep(step + 1)"
              @update:model-value="selectCurrentProvider"
              ref="stepComponent"
              :na="currentProviderNotApplicable"
              :providers="selectedProviders"
              :show-next="showNextOnStep(step)"
              :subsidy-program="subsidyProgram"
              :valid="currentProviderSelectionValid"
            />
          </v-stepper-vertical-item>
        </v-card-text>
      </v-card>

      <v-card
        v-if="conditionalSteps?.includes('sibling_provider_ids')"
        class="mb-4 w-100pc"
        data-testid="siblingSelectionStep"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <v-stepper-vertical-item
            :complete="step > conditionalSteps.indexOf('sibling_provider_ids')"
            :value="conditionalSteps.indexOf('sibling_provider_ids')"
          >
            <template #title>
              <h3 tabindex="0">
                {{ $bct(subsidyProgram.sibling_provider_title || '') }}
              </h3>
            </template>

            <SiblingSelectionStep
              v-model="siblingProviderIds"
              @back="changeStep(step - 1)"
              @na="siblingNotApplicableClicked"
              @next="changeStep(step + 1)"
              @update:model-value="selectSiblings"
              ref="stepComponent"
              :na="siblingNotApplicable"
              :providers="selectedProviders"
              :show-next="showNextOnStep(step)"
              :subsidy-program="subsidyProgram"
              :valid="siblingSelectionValid"
            />
          </v-stepper-vertical-item>
        </v-card-text>
      </v-card>
      <v-card
        v-if="conditionalSteps?.includes('staff_provider_ids')"
        class="mb-4 w-100pc"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <v-stepper-vertical-item
            :complete="step > conditionalSteps.indexOf('staff_provider_ids')"
            :value="conditionalSteps.indexOf('staff_provider_ids')"
          >
            <template #title>
              <h3 tabindex="0">
                {{ $bct(subsidyProgram.staff_provider_title || '') }}
              </h3>
            </template>

            <StaffSelectionStep
              v-model="staffProviderIds"
              @back="changeStep(step - 1)"
              @na="staffNotApplicableClicked"
              @update:model-value="selectStaff"
              ref="stepComponent"
              :na="staffNotApplicable"
              :providers="selectedProviders"
              :subsidy-program="subsidyProgram"
            />
          </v-stepper-vertical-item>
        </v-card-text>
      </v-card>
    </v-stepper-vertical>

    <ContentDialog
      ref="providerDialog"
      :max-width="800"
    >
      <template #content="{ data }">
        <ProviderMatch
          v-if="data"
          @change="$emit('change')"
          :match="data"
          :outlined="false"
          :schema="providerSchema"
        />
      </template>
    </ContentDialog>

    <ResourceDialog
      @save="confirmNoReferredProgram()"
      ref="noReferredProgramSelectedDialog"
      :cancellable="true"
      :closeable="true"
      :max-width="600"
      save-button-text="Proceed without the site selected"
      title="Are you sure you want to continue?"
    >
      <template #form>
        <v-row data-testid="noReferredProgramSelectedDialog">
          <v-col cols="12">
            <div class="fs-14">
              This individual has been recognized as either a present student, a sibling of a
              student, or a family member of someone employed by {{ referredProvider?.name }}. They
              were not listed among your choices. If you continue without selecting this location,
              your priority at this site will not be ensured.
            </div>
          </v-col>
        </v-row>
      </template>
    </ResourceDialog>

    <ResourceDialog
      @save="confirmNoReferredProviderInProviderSelections()"
      ref="noReferredProviderInSelectedProvidersDialog"
      :cancellable="true"
      :closeable="true"
      :max-width="600"
      save-button-text="Proceed without the provider selected"
      title="Are you sure you want to continue?"
    >
      <template #form>
        <v-row data-testid="noReferredProviderInSelectedProvidersDialog">
          <v-col cols="12">
            <div class="fs-14">
              This individual has been recognized as either a present student, a sibling of a
              student, or a family member of someone employed by {{ referredProvider?.name }}. You
              did not specify the provider as one that this child is presently a student of, has a
              sibling of a currently enrolled student, or a family member of a current employee. If
              you continue without selecting this location, your priority at this site will not be
              ensured.
            </div>
          </v-col>
        </v-row>
      </template>
    </ResourceDialog>

    <ResourceDialog
      @save="confirmNoGuaranteedSeatsDialog()"
      ref="noGuaranteedSeatsDialog"
      :cancellable="true"
      :closeable="true"
      :max-width="600"
      save-button-text="I understand"
      title="Seats are not guaranteed"
    >
      <template #form>
        <v-row data-testid="noGuaranteedSeatsDialog">
          <v-col cols="12">
            <div class="fs-14">
              {{
                $bct(
                  'Adding this program to your list does not reserve the seat. While the program has seats available now, they may be filled prior to placement.',
                )
              }}
            </div>
          </v-col>
        </v-row>
      </template>
    </ResourceDialog>
  </FormQuestion>
</template>

<script setup>
import CurrentEnrollmentStep from '@/parent/components/subsidy/CurrentEnrollmentStep.vue';
import ContentDialog from '@/shared/components/ContentDialog.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import ProviderMatch from '@/shared/components/search/ProviderMatch.vue';
import RankProgramsStep from '@/parent/components/subsidy/RankProgramsStep.vue';
import SelectProgramsStep from '@/parent/components/subsidy/SelectProgramsStep.vue';
import SiblingSelectionStep from '@/parent/components/subsidy/SiblingSelectionStep.vue';
import StaffSelectionStep from '@/parent/components/subsidy/StaffSelectionStep.vue';
import ProviderReferredProgramCard from '@/parent/components/subsidy/ProviderReferredProgramCard.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import { SUBSIDY_EVENTS } from '@/parent/services/constants';
import { VStepperVertical, VStepperVerticalItem } from 'vuetify/labs/VStepperVertical';

import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useRoute } from 'vue-router';
import useSequentialModals from '@/shared/composables/useSequentialModals';
import { useStore } from 'vuex';

const { handleSequentialModals, handleNextOrCloseSequentialModal } = useSequentialModals();

const props = defineProps({
  childData: {
    default: null,
    type: Object,
  },
  defaultQuery: {
    default: null,
    type: String,
  },
  hideActions: {
    default: null,
    type: Boolean,
  },
  initialStep: {
    default: 1,
    type: Number,
  },
  previousChildName: {
    default: null,
    type: String,
  },
  previousSubsidyProgramIds: {
    default: null,
    type: Array,
  },
  readonly: {
    default: false,
    type: Boolean,
  },
  processing: {
    default: false,
    type: Boolean,
  },
  showSelected: {
    default: false,
    type: Boolean,
  },
  subsidy: {
    default: null,
    type: Object,
  },
  subsidies: {
    default: null,
    type: Array,
  },
  subsidyProgram: {
    default: null,
    type: Object,
  },
  siblingProviders: {
    default: () => [],
    type: Array,
  },
  staffProviders: {
    default: () => [],
    type: Array,
  },
  currentProvider: {
    default: null,
    type: String,
  },
});

const emit = defineEmits([
  'back',
  'change',
  'next',
  'reorderPrograms',
  'selectedCurrentProviderId',
  'selectedProgramIds',
  'selectedSiblingIds',
  'selectedStaffIds',
]);

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const store = useStore();
const $bct = inject('translator');

const currentProviderId = ref(props.currentProvider);
const currentProviderNotApplicable = ref(false);
const noGuaranteedSeatsDialog = ref(null);
const noReferredProgramSelectedDialog = ref(null);
const noReferredProviderInSelectedProvidersDialog = ref(null);
const providerDialog = ref(null);
const providerSchema = store.state.schemas[props.subsidyProgram.provider_schema_id];
const referredProvider = ref(null);
const selectedProviders = ref([]);
const showReferredProviderCard = ref(true);
const siblingNotApplicable = ref(false);
const siblingProviderIds = ref(props.siblingProviders);
const staffNotApplicable = ref(false);
const staffProviderIds = ref(props.staffProviders);
const step = ref(props.initialStep);
const viewSelectedResults = ref(false);

const conditionalSteps = computed(() => {
  let steps = [0, 'select', 'rank']; // first 2 values don't matter, but are added so we get the correct index of next steps
  if (props.subsidyProgram?.enable_current_provider_selection) {
    steps.push('current_provider_id');
  }
  if (props.subsidyProgram?.enable_sibling_provider_selection) {
    steps.push('sibling_provider_ids');
  }
  if (props.subsidyProgram?.enable_staff_provider_selection) {
    steps.push('staff_provider_ids');
  }
  return steps;
});

const currentProviderSelectionValid = computed(() => {
  return !!currentProviderId.value || currentProviderNotApplicable.value === true;
});

const providerSelectionContainsReferredProviderId = computed(() => {
  const referredProviderId = referredProvider.value?.id;
  return (
    currentProviderId.value === referredProviderId ||
    siblingProviderIds.value.includes(referredProviderId) ||
    staffProviderIds.value.includes(referredProviderId)
  );
});

const hasEligibleSeatsSelected = computed(() => {
  // We need to match on each of the selectedProviders.programs and subsequently identify selected programs using subsidy.program_ids
  // then determine if the program contains a seatAvailability of either "available" or "low"
  // if there are any matches then return true
  const matches = [];

  selectedProviders.value.forEach((provider) => {
    const isMatched = props.subsidy.program_ids.some((programId) =>
      provider.programs.some(
        (providerProgram) =>
          providerProgram.id === programId &&
          (providerProgram.meta?.seat_availability_flag === 'available' ||
            providerProgram.meta?.seat_availability_flag === 'low'),
      ),
    );

    if (isMatched === true) {
      matches.push(isMatched);
    }
  });

  return matches.length > 0;
});

const nextDisabled = computed(() => {
  return (
    props.subsidy.program_ids.length === 0 ||
    (step.value < 3 && props.subsidyProgram.enable_current_provider_selection)
  );
});

const siblingSelectionValid = computed(() => {
  return siblingProviderIds.value.length > 0 || siblingNotApplicable.value === true;
});

const staffSelectionValid = computed(() => {
  return staffProviderIds.value.length > 0 || staffNotApplicable.value === true;
});

watch(
  siblingProviderIds,
  (newVal) => {
    // We need to emit sibling/staff selections back to the parent subsidy component due to a bug
    // which does not allow us to use v-bind directly through two ancestors
    emit('selectedSiblingIds', newVal);
  },
  { deep: true },
);

watch(
  staffProviderIds,
  (newVal) => {
    emit('selectedStaffIds', newVal);
  },
  { deep: true },
);

watch(currentProviderId, (newVal) => {
  emit('selectedCurrentProviderId', newVal);
});

watch(
  () => props.subsidy.program_ids,
  async (newVal) => {
    await loadSelectedProviders(newVal);
    updateReferredProgramVisibility(newVal);
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  await getProgram();
});

function updateReferredProgramVisibility(programIds = []) {
  if (programIds.length === 0) return;

  const referredProgramId = route.query.programId;
  const hasReferredProgramSelected = programIds.includes(referredProgramId);

  if (hasReferredProgramSelected) {
    showReferredProviderCard.value = false;
  } else {
    showReferredProviderCard.value = true;
  }
}

function handleReferredProgramConfirm(programId) {
  showReferredProviderCard.value = false;
  toggleSelection(programId, true);
}

async function getProgram() {
  const programId = route?.query?.programId;

  if (programId) {
    const resp = await api.public_api.organization.provider.index({
      program_ids: [programId],
      include: 'details, programs',
      page_size: 200,
    });

    const providers = resp.data;
    const provider = providers[0];
    const referredProgram = provider?.programs?.filter((program) => program.id === programId);

    referredProvider.value = { ...provider, programs: referredProgram };
  }
}

function validateAndNext() {
  if (currentProviderSelectionValid.value === false) {
    eventBus.chime(`A selection is required for ${props.subsidyProgram.current_provider_title}`);
    return;
  }

  if (
    props.subsidyProgram.enable_sibling_provider_selection &&
    siblingSelectionValid.value === false
  ) {
    eventBus.chime(`A selection is required for ${props.subsidyProgram.sibling_provider_title}`);
    return;
  }

  if (props.subsidyProgram.enable_staff_provider_selection && staffSelectionValid.value === false) {
    eventBus.chime(`A selection is required for ${props.subsidyProgram.staff_provider_title}`);
    return;
  }

  if (referredProvider.value && providerSelectionContainsReferredProviderId.value === false) {
    noReferredProviderInSelectedProvidersDialog.value.open();
    return;
  }

  emit('next');
}

function confirmNoReferredProviderInProviderSelections() {
  noReferredProviderInSelectedProvidersDialog.value.close();
  emit('next');
}

function selectCurrentProvider(providerId) {
  currentProviderId.value = providerId;

  if (currentProviderId.value && currentProviderNotApplicable.value === true) {
    currentProviderNotApplicable.value = false;
  }
}

function selectSiblings(selectedSiblingIds) {
  siblingProviderIds.value = selectedSiblingIds;

  if (selectedSiblingIds.length > 0 && siblingNotApplicable.value === true) {
    siblingNotApplicable.value = false;
  }
}

function siblingNotApplicableClicked() {
  siblingNotApplicable.value = !siblingNotApplicable.value;

  if (siblingNotApplicable.value) {
    siblingProviderIds.value = [];
  }
}

function selectStaff(selectedStaffIds) {
  staffProviderIds.value = selectedStaffIds;

  if (selectedStaffIds.length > 0 && staffNotApplicable.value === true) {
    staffNotApplicable.value = false;
  }
}

function staffNotApplicableClicked() {
  staffNotApplicable.value = !staffNotApplicable.value;

  if (staffNotApplicable.value) {
    staffProviderIds.value = [];
  }
}

function currentProviderNotApplicableClicked() {
  currentProviderNotApplicable.value = !currentProviderNotApplicable.value;

  if (currentProviderNotApplicable.value) {
    currentProviderId.value = null;
  }
}

function changeStep(stepNumber) {
  step.value = Math.max(0, Math.min(stepNumber, conditionalSteps.value.length - 1));
}

function showNextOnStep(currentStep) {
  return currentStep + 1 < conditionalSteps.value.length;
}

function validateStepChange(stepNumber) {
  const referredProgramId = referredProvider.value?.programs[0]?.id;
  const hasReferredProgramSelected =
    referredProgramId && props.subsidy.program_ids.includes(referredProgramId);

  const referredProgramValidator =
    step.value === 1 && referredProgramId && !hasReferredProgramSelected;
  const seatsAvailabilityValidator = step.value === 1 && hasEligibleSeatsSelected.value;

  return handleSequentialModals({
    modalRef: noReferredProgramSelectedDialog.value,
    modalValidator: referredProgramValidator,
    nextModalRef: noGuaranteedSeatsDialog.value,
    nextModalValidator: seatsAvailabilityValidator,
    proceedFn: () => changeStep(stepNumber),
  });
}

function confirmNoGuaranteedSeatsDialog() {
  handleNextOrCloseSequentialModal();
}

function confirmNoReferredProgram() {
  handleNextOrCloseSequentialModal();
}

function copyPreferences() {
  props.subsidy.program_ids = JSON.parse(JSON.stringify(props.previousSubsidyProgramIds));
  emit('change', props.subsidy.program_ids);
  viewSelectedResults.value = true;
}

async function loadSelectedProviders(programIds = []) {
  if (programIds.length === 0) return;
  // Use programIds arg for values to prevent race condition in state
  // subsidy.program_ids updating

  const resp = await api.public_api.organization.provider.index({
    program_ids: programIds,
    subsidy_program_id: props.subsidyProgram.id,
    include: 'details,programs',
    page_size: 200,
  });

  selectedProviders.value = resp.data;
}

function openProvider(providerId) {
  api.public_api.provider.get(providerId, (resp) => {
    providerDialog.value.open(resp.data);
  });
}

function remove(programId) {
  emit('selectedProgramIds', { type: SUBSIDY_EVENTS.REMOVE, id: programId });
}

function verifySelectionLimit(programId) {
  if (!props.subsidyProgram.selection_limit) return;

  if (
    props.subsidyProgram.selection_limit > 0 &&
    props.subsidyProgram.selection_limit <= props.subsidy.program_ids.length &&
    !props.subsidy.program_ids.includes(programId[0])
  ) {
    eventBus.chime('Exceeded selection limit');
  }
}

function toggleSelection(programId, newVal) {
  if (newVal === true) {
    if (!props.subsidy.program_ids.includes(programId)) {
      emit('selectedProgramIds', { type: SUBSIDY_EVENTS.ADD, id: programId });
    }
  }

  if (newVal === false && props.subsidy.program_ids.includes(programId)) {
    emit('selectedProgramIds', { type: SUBSIDY_EVENTS.REMOVE, id: programId });
  }

  emit('change', props.subsidy.program_ids);
}
</script>

<style scoped>
/* Former global style; remove and fix padding if necessary */
.full-width.v-stepper .v-stepper-vertical-item .v-expansion-panel-text,
.v-stepper-vertical-item .v-expansion-panel-text__wrapper {
  padding: 0 !important;
  padding-inline-start: 0 !important;
}
</style>
